import React from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';
import getTooltipStyles from '../../../../shared/helpers';
import { setCurrencyFormat, colorsCombination } from '../../../../utils/helpers';

export const CustomTooltip = (props) => {
  // eslint-disable-next-line react/prop-types
  const { active, payload } = props;
  // we don't need to check payload[0] as there's a better prop for this purpose
  if (!active) {
    // I think returning null works based on this: http://recharts.org/en-US/examples/CustomContentOfTooltip
    return null;
  }
  // mutating props directly is against react's conventions
  // so we create a new payload with the name and value fields set to what we want
  // eslint-disable-next-line react/prop-types

  const newPayload =
    payload &&
    Object.values(payload)
      .sort((a, b) => (a.value < b.value ? 1 : -1))
      .map(item => ({ ...item, value: setCurrencyFormat(Number(item.value).toFixed(2), true) }));

  // we render the default, but with our overridden payload
  return <DefaultTooltipContent {...props} payload={newPayload} />;
};

const returnKey = ({ item, currentlyShow }) => {
  const { salesDay, salesMonthStr, salesYear } = item;

  let key = '';
  if (currentlyShow?.value === 1) {
    key = `${salesDay}-${salesMonthStr}-${salesYear}`;
  }

  if (currentlyShow?.value === 2) {
    key = `${salesMonthStr}-${salesYear}`;
  }

  if (currentlyShow?.value === 3) {
    key = `${salesYear}`;
  }

  return key;
};

const reduce = (data, dataByDay, currentlyShow) => {
  data?.reduce((acc, item) => {
    const key = returnKey({ item, currentlyShow });

    if (acc[key]) {
      acc[key].push(item);
    } else {
      acc[key] = [item];
    }
    return acc;
  }, dataByDay);
};

const CombineAreaChart = ({ dir, themeName, currentlyShow }) => {
  const amazonDataByDay = {};
  const shopifyDataByDay = {};
  const wholesaleDataByDay = {};

  const { amazonRevenueData, shopifyRevenueData, wholesaleRevenueData } = useSelector(
    ({ dashboardReducer }) => ({
      amazonRevenueData: dashboardReducer.amazon.revenueData?.modal?.data,
      shopifyRevenueData: dashboardReducer.shopify.revenueData?.modal?.data,
      wholesaleRevenueData: dashboardReducer.wholesale.revenueData?.modal?.data,
    }),
  );

  reduce(amazonRevenueData || [], amazonDataByDay, currentlyShow);
  reduce(shopifyRevenueData || [], shopifyDataByDay, currentlyShow);
  reduce(wholesaleRevenueData || [], wholesaleDataByDay, currentlyShow);

  const amazonDataByDays = Object.keys(amazonDataByDay);
  const shopifyDataByDays = Object.keys(shopifyDataByDay);
  const wholesaleDataByDays = Object.keys(wholesaleDataByDay);

  const amazonDataByDaysCount = amazonDataByDays.length;
  const shopifyDataByDaysCount = shopifyDataByDays.length;
  const wholesaleDataByDaysCount = wholesaleDataByDays.length;

  const dataByDayMap = {
    [amazonDataByDaysCount]: amazonDataByDays,
    [shopifyDataByDaysCount]: shopifyDataByDays,
    [wholesaleDataByDaysCount]: wholesaleDataByDays,
  };

  let dataToPlot = [];

  const amazonChannelKeyMap = {
    'Amazon.com': 'Amazon US',
    'Amazon.co.uk': 'Amazon UK',
    'Amazon.ca': 'Amazon CA',
    'Amazon.fr': 'Amazon FR',
    'Amazon.de': 'Amazon DE',
    'Amazon.it': 'Amazon IT',
    'Amazon.es': 'Amazon SP',
  };

  const channelKeyMap = {
    total: 'Total',
    amazon: 'Amazon',
    shopify: 'Shopify',
    wholesale: 'Wholesale',
  };

  const maxDays = Math.max(amazonDataByDaysCount, shopifyDataByDaysCount, wholesaleDataByDaysCount);

  if (dataByDayMap?.[maxDays]?.forEach) {
    dataByDayMap?.[maxDays]?.forEach((day) => {
      const item = {
        day,
        Amazon: 0,
        Shopify: 0,
        Wholesale: 0,
        Total: 0,
      };

      const amazonDayData = amazonDataByDay[day];
      amazonDayData?.reduce((acc, dayItem) => {
        if (amazonChannelKeyMap[dayItem.salesChannel]) {
          item[channelKeyMap.amazon] += Number(dayItem.amount);
          item[channelKeyMap.total] += Number(dayItem.amount);
        }
        return acc;
      }, item);
      const shopifyDayData = shopifyDataByDay[day];

      shopifyDayData?.reduce((acc, dayItem) => {
        item[channelKeyMap.shopify] += Number(dayItem.amount);
        item[channelKeyMap.total] += Number(dayItem.amount);
        return acc;
      }, item);

      const wholesaleRevenueDayData = wholesaleDataByDay[day];

      wholesaleRevenueDayData?.reduce((acc, dayItem) => {
        item[channelKeyMap.wholesale] += Number(dayItem.amount);
        item[channelKeyMap.total] += Number(dayItem.amount);
        return acc;
      }, item);

      dataToPlot.push(item);
    });
  }
  console.log(dataToPlot)
  dataToPlot = dataToPlot.sort((a, b) => 
    Date.parse(new Date(a.day.split("-").reverse().join("-"))) - 
    Date.parse(new Date(b.day.split("-").reverse().join("-"))));
             
  return (
    <>
      <Row className="margin-negative-30 mt-2 pt-3 ">
        <Col sm="12" style={{ width: '100%', height: '415px' }} className="container-create pt-4">
          <div className="card__title mb-2">
            <h5 className="bold-text">Total Revenue Comparison</h5>
          </div>
          <ResponsiveContainer height={340} className="dashboard__area">
            <AreaChart data={dataToPlot} margin={{ top: 20, left: -15, bottom: 20 }}>
              <XAxis dataKey="day" tickLine={false} reversed={dir === 'rtl'} />
              <YAxis tickLine={false} orientation={dir === 'rtl' ? 'right' : 'left'} />
              <Tooltip
                {...getTooltipStyles(themeName, 'defaultItems')}
                content={<CustomTooltip />}
              />
              <Legend />
              <CartesianGrid vertical={false} horizontal stroke="#FF0000" strokeWidth={2} />
              {Object.keys(channelKeyMap).map((channel, index) => (
                <Area
                  key={channel}
                  name={channelKeyMap[channel]?.toString()}
                  type="monotone"
                  dataKey={channelKeyMap[channel]?.toString()}
                  fill={colorsCombination[index % 10][0] || '#4ce1b6'}
                  stroke={colorsCombination[index % 10][0] || '#4ce1b6'}
                  fillOpacity={0.2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Col>
      </Row>
    </>
  );
};

CombineAreaChart.propTypes = {
  dir: PropTypes.string.isRequired,
  themeName: PropTypes.string.isRequired,
  currentlyShow: PropTypes.objectOf({ key: '', value: '' }).isRequired,
};

export default connect(state => ({ themeName: state.theme.className }))(CombineAreaChart);
